<script setup>
import {
    eachDayOfInterval, endOfWeek, format, set, startOfWeek, setDefaultOptions, subDays, addDays, compareDesc, addWeeks,
    parse
} from "date-fns";
import {fr} from 'date-fns/locale';
import {ref, watch} from "vue";
import httpClient from "../../lib/http/index.js";
import arrayRemove from "../../lib/utils/arrayRemove.js";
import objectMap from "../../lib/utils/objectMap.js";
import objectFilter from "../../lib/utils/objectFilter.js";
setDefaultOptions( {locale: fr, weekStartsOn: 1} );

const emit = defineEmits(['step', 'change']);
const {courses, pricing, holidays, seasonStart} = defineProps(['courses', 'pricing', 'holidays', 'seasonStart']);

const parse_hour = hourStr => parse( hourStr, "HH:mm", new Date() );

const http = httpClient();

const courses_by_days = ref( [] ),
    selected_courses_items = ref( courses ),
    selected_courses_by_date = ref( [] ),
    selected_courses = ref( Object.values( objectMap( courses, course => course.scheduled_date ) ) ),
    actual_hour = set( new Date, {} ),
    season_start_date = set( parse( seasonStart, 'yyyy-MM-dd', new Date ),{ hours: 0, minutes: 0, seconds: 0, milliseconds: 0 } ),
    end_of_subscription_date = subDays( endOfWeek( startOfWeek( addWeeks( season_start_date, 8 ) ) ), 1 ),
    current_date = ref( set( season_start_date, { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 } ) ),
    loading = ref( false ),
    show_dance = ref( true ),
    show_fitness = ref( true ),
    pricing_dance = pricing.find( pricing => pricing.course_type === 'dance' ),
    pricing_fitness = pricing.find( pricing => pricing.course_type === 'fitness' );

let fetch_courses_signal = null,
    start = startOfWeek( current_date.value ),
    end = subDays( endOfWeek( current_date.value ), 1 ),
    days = eachDayOfInterval( { start, end } );

Object.values( courses ).forEach( course => {
    if ( course.course_type === 'dance' ) {
        const date = parse( course.scheduled_date, 'yyyy-MM-dd HH:mm', new Date );

        let day_date = null,
            day_date_hourly = null,
            nbr_courses = 0,
            i = 0;

        while ( nbr_courses < 3 ) {
            day_date = format( addWeeks( date, i ), 'yyyy-MM-dd' );
            day_date_hourly = format( addWeeks( date, i ), 'yyyy-MM-dd HH:mm' );
            i++;

            if ( ! holidays.includes( day_date ) ) {
                nbr_courses++;
                selected_courses_by_date.value.push( day_date_hourly );
            }
        }
    }
} );

function selectCourse(course) {
    const date = parse( course.scheduled_date, 'yyyy-MM-dd HH:mm', new Date );

    if ( ! selected_courses.value.includes( course.scheduled_date ) ) {
        selected_courses.value.push( course.scheduled_date );
        selected_courses_items.value[ course.scheduled_date ] = course;

        if ( course.course_type === 'dance' ) {
            let day_date = null,
                day_date_hourly = null,
                nbr_courses = 0,
                i = 0;

            while ( nbr_courses < 3 ) {
                day_date = format( addWeeks( date, i ), 'yyyy-MM-dd' );
                day_date_hourly = format( addWeeks( date, i ), 'yyyy-MM-dd HH:mm' );
                i++;

                if ( ! holidays.includes( day_date ) ) {
                    nbr_courses++;
                    selected_courses_by_date.value.push( day_date_hourly );
                }
            }
        }

    } else if ( selected_courses.value.includes( course.scheduled_date ) ) {
        selected_courses.value = arrayRemove( selected_courses.value, course.scheduled_date );
        delete selected_courses_items.value[ course.scheduled_date ];

        if ( course.course_type === 'dance' ) {
            let day_date = null,
                day_date_hourly = null,
                nbr_courses = 0,
                i = 0;

            while ( nbr_courses < 3 ) {
                day_date = format( addWeeks( date, i ), 'yyyy-MM-dd' );
                day_date_hourly = format( addWeeks( date, i ), 'yyyy-MM-dd HH:mm' );
                i++;

                if ( ! holidays.includes( day_date ) ) {
                    nbr_courses++;
                    selected_courses_by_date.value = arrayRemove( selected_courses_by_date.value, day_date_hourly );
                }
            }
        }
    }

    emit( 'change', selected_courses_items.value );
}

function previousWeek() {
    current_date.value = subDays( current_date.value, 7 );
}

function nextWeek() {
    current_date.value = addDays( current_date.value, 7 );
}

function actualWeek() {
    current_date.value = structuredClone( season_start_date );
}

function reset() {
    selected_courses.value = [];
    selected_courses_items.value = {};
    selected_courses_by_date.value = [];
}

async function fetchCourses() {
    try {
        if ( fetch_courses_signal ) {
            fetch_courses_signal.abort('Cancel request');
        }

        fetch_courses_signal = http.signal();

        const params = new URLSearchParams({
            start: format( start, 'yyyy-MM-dd'),
            end: format( end, 'yyyy-MM-dd'),
            course_type: ['dance', 'fitness'],
        } );

        let response = await http.get('/api/v1/courses/scheduled?' + params.toString(), {}, {
            signal: fetch_courses_signal.signal
        } );

        fetch_courses_signal = null;

        return courses_by_days.value = response.body;
    } catch (e) {
    }
}

async function fetch() {
    loading.value = true;

    await fetchCourses();

    loading.value = false;
}

watch( current_date, async new_date => {
    start = startOfWeek( new_date );
    end = subDays( endOfWeek( new_date ), 1 );
    days = eachDayOfInterval( { start, end } );

    await fetch();
} );

(async () => {
    await fetch();
})();
</script>

<template>
    <div class="w-full flex flex-col">
        <div class="flex flex-row mb-10">
            <h2 class="text-white text-4xl text-center flex-1">Choix des cours</h2>
        </div>

        <div class="flex flex-col gap-5 mb-6">
            <p class="text-white">
                Vous pouvez sélectionner le ou les cours de votre choix.
            </p>

            <p class="text-white">
                <strong class="underline">Attention</strong> :
                Chaque réservation est individuelle.<br/>Vous trouverez un récapitulatif des cours que vous avez sélectionnés en bas du tableau.<br/><br/>
                Chaque réservation à un cours de danse coûte <span class="border-b">30 &euro;</span> pour une période de 3 semaines et<br/>
                chaque réservation à un cours de fitness coûte <span class="border-b">10 &euro;</span> pour une période de 1 semaine.
            </p>

            <div class="flex flex-col xl:flex-row justify-between mt-4">
                <div class="flex flex-row justify-center mb-10 xl:mb-0">
                    <label class="text-white cursor-pointer">
                        <input type="checkbox" class="inline-block mr-2 relative top-0.5 h-4 w-4" v-model="show_dance" :disabled="!show_fitness && show_dance">
                        <span>Afficher la danse</span>
                    </label>

                    <label class="text-white cursor-pointer ml-12">
                        <input type="checkbox" class="inline-block mr-2 relative top-0.5 h-4 w-4" v-model="show_fitness" :disabled="!show_dance && show_fitness">
                        <span>Afficher le fitness</span>
                    </label>
                </div>

                <div class="flex flex-col sm:flex-row xl:w-1/2 justify-between xl:justify-end">
                    <div class="text-white text-sm mr-5 leading-loose mb-5 sm:mb-0 sm:w-1/2">
                        Semaine du <span class="font-bold border-b">{{ format( start, 'dd LLLL yyyy' ) }}</span> au <span class="font-bold border-b">{{ format( end, 'dd LLLL yyyy' ) }}</span>
                    </div>

                    <div class="flex flex-row justify-end sm:w-1/2">
                        <button class="px-1.5 py-1 mr-5 text-sm text-white rounded-md border border-white/10 hover:bg-orange transition-colors"
                                :class="{
                                    'opacity-10 pointer-events-none': compareDesc( current_date, season_start_date ) === 0,
                                }" @click="actualWeek" type="button" name="Cette semaine">
                            <i class="fa-solid fa-fw fa-calendar-week mr-0.5"></i> Cette semaine
                        </button>

                        <button class="px-1.5 py-1 mr-2.5 text-sm text-white rounded-md border border-white/10 hover:bg-orange transition-colors "
                                :class="{
                                    'opacity-10 pointer-events-none': compareDesc( current_date, season_start_date ) >= 0,
                                }" @click="previousWeek" type="button" name="Semaine précédente">
                            <i class="fa-solid fa-fw fa-chevron-left"></i>
                        </button>

                        <button class="px-1.5 py-1 text-sm text-white rounded-md border border-white/10 hover:bg-orange transition-colors"
                                :class="{
                                'opacity-10 pointer-events-none': compareDesc( end_of_subscription_date, end ) >= 0,
                            }" @click="nextWeek" type="button" name="Semaine suivante">
                            <i class="fa-solid fa-fw fa-chevron-right"></i>
                        </button>
                    </div>
                </div>
            </div>

            <div class="flex flex-col lg:flex-row w-full gap-x-2" :class="{'pointer-events-none opacity-25': loading}">
                <div v-for="(date, i) in days" :key="i"
                     class="flex flex-col w-full"
                     :class="{
                        'hidden': courses_by_days[ i ] === undefined
                     }">
                    <div class="bg-purple border-b-2 text-center text-white content-center uppercase p-4 mb-5">
                        <span>{{ format( date, 'eeee' ) }}</span>
                        <br/>
                        <span class="text-xs">{{ format( date, 'dd LLLL' ) }}</span>
                    </div>

                    <div class="text-white text-center py-6 px-2 2xl:px-4 mb-2 transition-colors border border-transparent"
                         v-if="holidays.includes( format( date, 'yyyy-MM-dd') )">
                        <p class="font-bold uppercase">Cours non programmé</p>
                    </div>

                    <div v-if="!holidays.includes( format( date, 'yyyy-MM-dd') )"
                        v-for="(course, index) in courses_by_days[ i ]" :key="index">
                        <div
                            @click="() => selectCourse( course )"
                            class="text-white text-center p-2 2xl:px-4 mb-2 cursor-pointer transition-all border border-transparent"
                            :class="{
                                'hidden': ! ( show_dance && course.course_type === 'dance' ) && ! ( show_fitness && course.course_type === 'fitness' ),
                                'bg-dance/40 border-white/50': selected_courses.includes( course.scheduled_date ) && course.course_type === 'dance',
                                'bg-fitness/40 border-white/50': selected_courses.includes( course.scheduled_date ) && course.course_type === 'fitness',
                                'bg-dance hover:border-white': !selected_courses.includes( course.scheduled_date ) && course.course_type === 'dance',
                                'bg-fitness hover:border-white': !selected_courses.includes( course.scheduled_date ) && course.course_type === 'fitness',
                                'bg-dance/40 border-white/50 pointer-events-none opacity-50': !selected_courses.includes( course.scheduled_date ) && selected_courses_by_date.includes( course.scheduled_date ) && course.course_type === 'dance',
                                'opacity-10 pointer-events-none':
                                season_start_date > date || season_start_date >= date && actual_hour >= parse_hour( course.schedule_end ) || course.is_full
                            }">
                            <span class="text-xs">{{ course.schedule_start }} - {{ course.schedule_end }}</span>
                            <p class="font-bold uppercase">{{ course.title }}</p>
                            <p class="text-xs" v-if="!course.is_full">{{ course.nbr_places_try - course.count_places }} places</p>
                            <p class="text-xs" v-else>Complet</p>
                        </div>
                    </div>
                </div>
            </div>

            <p class="text-white text-center text-lg">
                <span class="block" v-if="pricing_dance">Cours de danse : <span class="strong">{{ Object.values( objectFilter( selected_courses_items, course => course.course_type === 'dance' ) ).length * pricing_dance.price }} €</span> pour 3 semaines d'essai</span>
                <span class="block" v-if="pricing_fitness">Cours de fitness : <span class="strong">{{ Object.values( objectFilter( selected_courses_items, course => course.course_type === 'fitness' ) ).length * pricing_fitness.price }} €</span> pour 1 semaine d'essai</span>
            </p>
        </div>

        <div class="text-center flex flex-row gap-x-5 justify-center">
            <button class="button-text | px-5 py-3" type="button" :disabled="selected_courses.length === 0"
                    @click="reset" name="Réinitialiser">Réinitialiser <i class="fa-solid fa-rotate-left | ml-2"></i></button>

            <button class="button | px-5 py-3" type="button" :disabled="selected_courses.length === 0"
                    @click="emit('step', 'account');" name="Suivant">Suivant <i class="fa-solid fa-angle-right | ml-2"></i></button>
        </div>
    </div>
</template>